/**
 * Error Fallback Component
 *
 * Displays user-friendly error UI when ErrorBoundary catches an error.
 * Shows error details in development mode only.
 */

import React, { ErrorInfo } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface ErrorFallbackProps {
  error: Error | null;
  errorInfo?: ErrorInfo | null;
  onReset?: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, errorInfo, onReset }) => {
  const isDev = import.meta.env.DEV;

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleRetry = () => {
    if (onReset) {
      onReset();
    } else {
      window.location.reload();
    }
  };

  const handleReportIssue = () => {
    // TODO: Implement issue reporting (e.g., email, support ticket, error tracking)
    const subject = encodeURIComponent(`Error Report: ${error?.message || 'Unknown Error'}`);
    const body = encodeURIComponent(
      `Please describe what you were doing when this error occurred:\n\n` +
        `---\n` +
        `Error: ${error?.message || 'Unknown'}\n` +
        `Stack: ${error?.stack || 'N/A'}\n` +
        `User Agent: ${navigator.userAgent}\n` +
        `Timestamp: ${new Date().toISOString()}`
    );
    window.location.href = `mailto:support@wicloud.com?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas p-4">
      <Card className="max-w-2xl w-full p-8">
        <div className="flex flex-col items-center text-center space-y-6">
          {/* Icon */}
          <div className="rounded-full bg-destructive-soft p-4">
            <AlertTriangle className="w-12 h-12 text-destructive" />
          </div>

          {/* Title */}
          <div className="space-y-2">
            <h1 className="text-xl font-semibold tracking-tight text-ink md:text-2xl">
              Oops! Something went wrong
            </h1>
            <p className="text-sm text-muted">
              We apologize for the inconvenience. An unexpected error has occurred.
            </p>
          </div>

          {/* Error details (dev mode only) */}
          {isDev && error && (
            <div className="w-full bg-panel-2 rounded-lg p-4 text-left">
              <div className="space-y-2">
                <div>
                  <h3 className="text-sm font-semibold text-ink-2 mb-1">
                    Error Message:
                  </h3>
                  <p className="text-sm text-destructive font-mono">
                    {error.message}
                  </p>
                </div>

                {error.stack && (
                  <div>
                    <h3 className="text-sm font-semibold text-ink-2 mb-1">
                      Stack Trace:
                    </h3>
                    <pre className="text-xs text-muted font-mono overflow-x-auto whitespace-pre-wrap">
                      {error.stack}
                    </pre>
                  </div>
                )}

                {errorInfo?.componentStack && (
                  <div>
                    <h3 className="text-sm font-semibold text-ink-2 mb-1">
                      Component Stack:
                    </h3>
                    <pre className="text-xs text-muted font-mono overflow-x-auto whitespace-pre-wrap">
                      {errorInfo.componentStack}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Button onClick={handleRetry} variant="default" className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Try Again
            </Button>

            <Button onClick={handleGoHome} variant="outline" className="gap-2">
              <Home className="w-4 h-4" />
              Go to Home
            </Button>

            <Button onClick={handleReportIssue} variant="outline">
              Report Issue
            </Button>
          </div>

          {/* Help text */}
          <p className="text-sm text-muted">
            If this problem persists, please contact our support team.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default ErrorFallback;
